import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import ProtectedRoute from '../../routes/protected'
import SidebarCargosSalarios from '../../components/sidebars/sidebarCargosSalarios'
import salaryAnalysisService from '../../services/data/salary-analysis.service'
import companyService from '../../services/data/company.service'
import currencyFormat from '../../helpers/currencyFormat'
import useCompanyData from '../../context/company'
import UsePerms from '../../context/userAccess'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Toast } from 'primereact/toast'
import { Dropdown } from 'primereact/dropdown'

function bandHint(amount, minV, midV, maxV) {
    const a = Number(amount)
    if (!a || minV === null || maxV === null) return null
    if (a < minV) return { label: 'Abaixo da faixa mínima', cls: 'warning' }
    if (a > maxV) return { label: 'Acima da faixa máxima', cls: 'danger' }
    return { label: 'Dentro da faixa da posição', cls: 'success' }
}

export default function RegistrarSalarios() {
    const toast = useRef(null)
    const [searchParams] = useSearchParams()
    const { rootUser } = useCompanyData()
    const VerifyPerms = UsePerms()
    const canManageOthers = VerifyPerms(['change_jobposition']) || rootUser

    const [rows, setRows] = useState([])
    const [count, setCount] = useState(0)
    const [page, setPage] = useState(1)
    const openedFromQuery = useRef(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    const [subcompanySel, setSubcompanySel] = useState(null)
    const [departmentSel, setDepartmentSel] = useState(null)
    const [subcompanyItems, setSubcompanyItems] = useState([])
    const [departmentItems, setDepartmentItems] = useState([])

    const [modalOpen, setModalOpen] = useState(false)
    const [activeRow, setActiveRow] = useState(null)
    const [amount, setAmount] = useState('')
    const [effectiveDate, setEffectiveDate] = useState('')
    const [reason, setReason] = useState('')
    const [submitting, setSubmitting] = useState(false)

    function buildFilters() {
        const f = {}
        if (subcompanySel) f.subcompany_id = subcompanySel.id
        if (departmentSel) f.department_id = departmentSel.id
        return f
    }

    function loadRows(p = 1, filters = {}) {
        setLoading(true)
        setError('')
        salaryAnalysisService.listEmployeesWithoutSalary(p, filters)
            .then(r => {
                setRows(r.results || [])
                setCount(r.count || 0)
            })
            .catch(e => setError(JSON.stringify(e?.response?.data || e)))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        companyService.autoCompleteSubCompany('')
            .then(r => setSubcompanyItems(r || []))
            .catch(() => setSubcompanyItems([]))
        companyService.autoCompleteDepartment('')
            .then(r => setDepartmentItems(r || []))
            .catch(() => setDepartmentItems([]))
    }, [])

    useEffect(() => {
        loadRows(page, buildFilters())
    }, [page])

    useEffect(() => {
        const emp = searchParams.get('employee')
        if (!emp || openedFromQuery.current || rows.length === 0) return
        const match = rows.find(r => r.employee_id === emp)
        if (match) {
            openedFromQuery.current = true
            openModal(match)
        }
    }, [searchParams, rows])

    function applyFilters() {
        const f = buildFilters()
        if (page === 1) loadRows(1, f)
        else setPage(1)
    }

    function clearFilters() {
        setSubcompanySel(null)
        setDepartmentSel(null)
        if (page === 1) loadRows(1, {})
        else setPage(1)
    }

    function openModal(row) {
        setActiveRow(row)
        setAmount('')
        setReason('')
        const d = new Date()
        setEffectiveDate(d.toISOString().slice(0, 10))
        setModalOpen(true)
    }

    function closeModal() {
        setModalOpen(false)
        setActiveRow(null)
    }

    function submitSalary(e) {
        e.preventDefault()
        if (!activeRow) return
        const val = String(amount).replace(',', '.').trim()
        const num = parseFloat(val)
        if (!num || num <= 0) {
            toast.current?.show({ severity: 'warn', summary: 'Valor inválido', detail: 'Informe um salário maior que zero.', life: 4000 })
            return
        }
        if (!effectiveDate) {
            toast.current?.show({ severity: 'warn', summary: 'Data', detail: 'Informe a data de vigência.', life: 4000 })
            return
        }
        setSubmitting(true)
        const payload = {
            employee: activeRow.employee_id,
            amount: num,
            effective_date: effectiveDate,
            currency: 'BRL',
            reason: reason || (canManageOthers ? 'Registro / enquadramento' : 'Autodeclaração de salário'),
        }
        if (activeRow.primary_jobposition_id) {
            payload.jobposition = activeRow.primary_jobposition_id
        }
        salaryAnalysisService.createEmployeeSalary(payload)
            .then(() => {
                toast.current?.show({ severity: 'success', summary: 'Salário registrado', detail: 'O registro foi salvo com sucesso.', life: 4000 })
                closeModal()
                loadRows(page, buildFilters())
            })
            .catch(err => {
                const msg = err?.response?.data ? JSON.stringify(err.response.data) : String(err)
                toast.current?.show({ severity: 'error', summary: 'Erro', detail: msg, life: 6000 })
            })
            .finally(() => setSubmitting(false))
    }

    const hint = activeRow ? bandHint(amount, activeRow.min_salary, activeRow.mid_salary, activeRow.max_salary) : null

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />
            <div className="container-fluid">
                <div className="row flex-nowrap">
                    <SidebarCargosSalarios />
                    <div className="col-xs-10 col-sm-9 col-md-9 col-xl-10 p-0 formMarginBottom">
                        <div className="col text-bg-green p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <span className="fs-6 fw-bold">Registrar salários</span>
                        </div>

                        <div className="alert alert-light border mx-3 mt-3 mb-0 small" role="note">
                            <FontAwesomeIcon icon={solid('lock')} className="me-2 text-muted" />
                            <strong>Privacidade:</strong> valores salariais são tratados como dados sensíveis.
                            {!canManageOthers && (
                                <span> Você vê apenas o seu cadastro. A listagem completa é restrita a quem possui permissão de gestão de cargos ou ao dono da empresa.</span>
                            )}
                            {canManageOthers && (
                                <span> Acesso a esta lista requer permissão de alteração de cargos ou perfil de dono da empresa.</span>
                            )}
                        </div>

                        {canManageOthers && (
                            <div className="row m-3 g-2 align-items-end">
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold">Organização</label>
                                    <Dropdown
                                        className="w-100"
                                        placeholder="Filtrar por organização"
                                        value={subcompanySel}
                                        options={subcompanyItems}
                                        optionLabel="title"
                                        filter
                                        showClear
                                        onChange={e => setSubcompanySel(e.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold">Departamento</label>
                                    <Dropdown
                                        className="w-100"
                                        placeholder="Filtrar por departamento"
                                        value={departmentSel}
                                        options={departmentItems}
                                        optionLabel="title"
                                        filter
                                        showClear
                                        onChange={e => setDepartmentSel(e.value)}
                                    />
                                </div>
                                <div className="col-auto">
                                    <button type="button" className="btn btn-success btn-sm" onClick={applyFilters}>
                                        <FontAwesomeIcon icon={solid('magnifying-glass')} /> Filtrar
                                    </button>
                                </div>
                                <div className="col-auto">
                                    <button type="button" className="btn btn-link btn-sm text-grey text-decoration-none" onClick={clearFilters}>
                                        Limpar
                                    </button>
                                </div>
                            </div>
                        )}

                        {error && <div className="alert alert-danger mx-3">{error}</div>}

                        {loading && (
                            <div className="text-center p-5">
                                <div className="spinner-border text-success" role="status" />
                            </div>
                        )}

                        {!loading && (
                            <div className="m-3">
                                <div className="card border-clean">
                                    <div className="card-body p-0">
                                        <div className="p-3 border-bottom small text-muted">
                                            {count === 0
                                                ? 'Nenhum colaborador alocado em posição sem salário registrado neste filtro.'
                                                : `${count} colaborador(es) sem registro de salário.`}
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-hover table-sm mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Colaborador</th>
                                                        <th>Posição(ões)</th>
                                                        <th className="text-end">Faixa mín.</th>
                                                        <th className="text-end">Midpoint</th>
                                                        <th className="text-end">Faixa máx.</th>
                                                        <th />
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows.map(row => (
                                                        <tr key={row.employee_id}>
                                                            <td className="align-middle fw-semibold">{row.employee_name}</td>
                                                            <td className="align-middle small">
                                                                {(row.position_titles || []).join(' · ') || '—'}
                                                            </td>
                                                            <td className="align-middle text-end small">
                                                                {row.min_salary !== null ? currencyFormat(row.min_salary) : '—'}
                                                            </td>
                                                            <td className="align-middle text-end small">
                                                                {row.mid_salary !== null ? currencyFormat(row.mid_salary) : '—'}
                                                            </td>
                                                            <td className="align-middle text-end small">
                                                                {row.max_salary !== null ? currencyFormat(row.max_salary) : '—'}
                                                            </td>
                                                            <td className="align-middle text-end">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-success btn-sm btn-rounded"
                                                                    onClick={() => openModal(row)}
                                                                >
                                                                    Registrar
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {rows.length > 0 && (
                                            <div className="p-2 border-top d-flex justify-content-end">
                                                <nav>
                                                    <ul className="pagination pagination-sm mb-0">
                                                        <li className="page-item">
                                                            <button
                                                                type="button"
                                                                className="page-link"
                                                                disabled={page === 1 || loading}
                                                                onClick={() => setPage(page - 1)}
                                                            >
                                                                <FontAwesomeIcon icon={solid('angle-left')} />
                                                            </button>
                                                        </li>
                                                        <li className="page-item">
                                                            <span className="page-link text-muted">{page}</span>
                                                        </li>
                                                        <li className="page-item">
                                                            <button
                                                                type="button"
                                                                className="page-link"
                                                                disabled={rows.length < 50 || loading}
                                                                onClick={() => setPage(page + 1)}
                                                            >
                                                                <FontAwesomeIcon icon={solid('angle-right')} />
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {modalOpen && activeRow && (
                <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.35)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-clean">
                            <div className="modal-header">
                                <h6 className="modal-title fw-bold">Registrar salário — {activeRow.employee_name}</h6>
                                <button type="button" className="btn-close" onClick={closeModal} aria-label="Fechar" />
                            </div>
                            <form onSubmit={submitSalary}>
                                <div className="modal-body">
                                    <p className="small text-muted">
                                        Faixa da posição (referência):{' '}
                                        {activeRow.min_salary !== null
                                            ? `${currencyFormat(activeRow.min_salary)} — ${currencyFormat(activeRow.mid_salary)} — ${currencyFormat(activeRow.max_salary)}`
                                            : 'Sem estrutura salarial vinculada à posição.'}
                                    </p>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Salário (R$)</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            value={amount}
                                            onChange={e => setAmount(e.target.value)}
                                            placeholder="Ex.: 7500,00"
                                        />
                                        {hint && (
                                            <div className={`mt-1 small text-${hint.cls}`}>
                                                <FontAwesomeIcon icon={solid('circle-info')} className="me-1" />
                                                {hint.label}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Data de vigência</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={effectiveDate}
                                            onChange={e => setEffectiveDate(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="mb-0">
                                        <label className="form-label small fw-bold">Motivo / observação (opcional)</label>
                                        <textarea
                                            className="form-control form-control-sm"
                                            rows={2}
                                            value={reason}
                                            onChange={e => setReason(e.target.value)}
                                            placeholder="Ex.: Promoção, reajuste anual, enquadramento pós-movimentação..."
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="submit" className="btn btn-success btn-sm" disabled={submitting}>
                                        {submitting ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </ProtectedRoute>
    )
}
